/**
 * Pick the subset of room walls that belongs to the kitchen "section"
 * of an open-plan room. Some scans store one room with multiple
 * sections (livingRoom + kitchen + diningRoom share a single floor
 * polygon and a single wall list). The kitchen-layout pipeline then
 * tries to fill cabinets along every wall of the open space and runs
 * into collisions across the dining / living portions.
 *
 * Strategy: each section carries a `center` point. For every wall,
 * project a probe a short distance into the room along the wall's
 * inward normal and assign the wall to whichever section's center
 * is closest to that probe. Pick the walls assigned to the
 * kitchen-labelled section. Cheap, no polygon, no graph search.
 *
 * For single-section rooms this is a no-op and returns every wall.
 *
 * The newer `getKitchenFinalWalls` clips each wall to the portion
 * geodesically inside the kitchen's MAIN reachable component (excludes
 * isolated < 3' sub-regions) and is what the layout pipeline now uses.
 *
 * Pure: returns a new array of walls.
 */

// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const MATH_CEIL = Math.ceil;
const { hypot2 } = require('../geometry/hypot2');
const MATH_MAX = Math.max;

const { getWallAxes } = require('./wallMath');
const { buildReachabilityMap } = require('../geometry/reachableRegions');
const { resolveKitchenRegion } = require('./kitchenRegion');

const PROBE_OFFSET = 0.3; // 30 cm — far enough to land inside the section,
//                            short enough to stay close to the wall surface.

function squaredDistance(a, b) {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}

/**
 * Assign each wall to the nearest section by probe distance.
 *
 * @param {Object} roomData
 * @returns {Map<string, Array<Object>>} sectionLabel → walls
 */
function assignWallsToSections(roomData) {
  const walls = roomData?.walls || [];
  const sections = (roomData?.sections || []).filter((s) => s?.center);
  const result = new Map();
  if (sections.length === 0) {
    result.set(null, walls);
    return result;
  }
  for (const s of sections) result.set(s.label || null, []);

  for (const wall of walls) {
    const axes = getWallAxes(wall);
    if (!axes) continue;
    const midX = (axes.start.x + axes.end.x) / 2;
    const midZ = (axes.start.z + axes.end.z) / 2;
    const probe = {
      x: midX + PROBE_OFFSET * axes.wallNormal.x,
      z: midZ + PROBE_OFFSET * axes.wallNormal.z,
    };
    let bestLabel = null;
    let bestDist = Infinity;
    for (const s of sections) {
      const d = squaredDistance(probe, s.center);
      if (d < bestDist) {
        bestDist = d;
        bestLabel = s.label || null;
      }
    }
    const list = result.get(bestLabel);
    if (list) list.push(wall);
  }
  return result;
}

/**
 * Return only the walls assigned to a section labelled 'kitchen'. If
 * the room has no sections, or no kitchen section, returns the full
 * wall list (the algorithm falls back to its previous all-walls
 * behavior).
 */
function getKitchenSectionWalls(roomData) {
  const walls = roomData?.walls || [];
  const sections = roomData?.sections || [];
  if (sections.length <= 1) return walls;
  const kitchenSections = sections.filter(
    (s) => s?.label && s.label.toLowerCase() === 'kitchen'
  );
  if (kitchenSections.length === 0) return walls;
  const byLabel = assignWallsToSections(roomData);
  const out = [];
  for (const s of kitchenSections) {
    const list = byLabel.get(s.label);
    if (list) out.push(...list);
  }
  return out.length > 0 ? out : walls;
}

/**
 * Resolve the polygon(s) that bound the kitchen's usable area.
 * Delegates to `resolveKitchenRegion` (the single source of truth):
 * an explicit `room.kitchenArea` override wins; otherwise the kitchen
 * Voronoi cell for multi-section rooms, or the whole floor polygon for
 * single-section rooms.
 *
 * Returns `null` when no region can be resolved (e.g. missing floor data).
 */
function getKitchenBoundaryPolygons(roomData) {
  const { polygons } = resolveKitchenRegion(roomData);
  return polygons.length > 0 ? polygons : null;
}

/**
 * Sample-based clipper: for each wall, return the [t1, t2] runs along
 * its length whose 30 cm-inward probe lands in the kitchen's MAIN
 * reachable component. `t` is parametric (0 at bottomLeft, 1 at
 * bottomRight). Returned `runs` are sorted longest-first so callers
 * that want a single canonical clip can take `runs[0]`.
 *
 * Returns `null` when the kitchen has no usable boundary polygon (in
 * which case the layout pipeline should fall back to the unclipped wall).
 */
function distancePointToSegment2D(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  if (len2 === 0) return hypot2(p.x - a.x, p.z - a.z);
  let t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return hypot2(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

function distanceToPolygonBoundary(point, polygons) {
  let minD = Infinity;
  for (const poly of polygons) {
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const d = distancePointToSegment2D(point, a, b);
      if (d < minD) minD = d;
    }
  }
  return minD;
}

// Wall sample must lie within this distance of a polygon boundary
// edge to count as a real kitchen-perimeter wall. Catches walls whose
// stored geometry over-extends past the room corner — physically
// continuing into an adjoining space — and would otherwise place
// cabinets along the wall's interior-floating portion.
const WALL_ON_BOUNDARY_TOLERANCE = 0.1; // 10 cm

/**
 * The kitchen region plus one reachability map per polygon — everything
 * `findKitchenWallRuns` needs that depends on the ROOM rather than the wall.
 * Built once per `getKitchenFinalWalls` call and handed to every wall: the
 * map is a grid over the polygon, and rebuilding it per wall was most of a
 * kitchen materialize once that ran per layout (#2069). Returns `null` when
 * no region resolves.
 */
function prepareKitchenRegion(roomData) {
  const { polygons, source } = resolveKitchenRegion(roomData);
  if (!polygons || polygons.length === 0) return null;
  return {
    polygons,
    source,
    maps: polygons.map((poly) => buildReachabilityMap(poly)),
  };
}

function findKitchenWallRuns(
  roomData,
  wall,
  sampleStep = 0.025,
  region = prepareKitchenRegion(roomData)
) {
  if (!region) return null;
  const { polygons: kitchenPolygons, source, maps } = region;
  // User-drawn override: keep any wall whose inward side opens into the
  // region (containment), instead of requiring the region edge to hug the
  // wall. Derived Voronoi/floor polygons keep boundary-based detection —
  // they already trace the walls, and relaxing them would over-include
  // interior walls / over-extended wall portions.
  const enforceBoundary = source !== 'override';
  const axes = getWallAxes(wall);
  if (!axes) return [];
  const a = axes.start;
  const b = axes.end;
  const len = axes.wallLen;
  const n = MATH_MAX(1, MATH_CEIL(len / sampleStep));
  const flags = new Array(n + 1);
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const sx = a.x + t * (b.x - a.x);
    const sz = a.z + t * (b.z - a.z);
    const sample = { x: sx, z: sz };
    if (
      enforceBoundary &&
      distanceToPolygonBoundary(sample, kitchenPolygons) >
        WALL_ON_BOUNDARY_TOLERANCE
    ) {
      flags[i] = false;
      continue;
    }
    const px = sx + PROBE_OFFSET * axes.wallNormal.x;
    const pz = sz + PROBE_OFFSET * axes.wallNormal.z;
    let comp = -1;
    for (const m of maps) {
      const c = m.classify({ x: px, z: pz });
      if (c >= 0) {
        comp = c;
        break;
      }
    }
    flags[i] = comp === 0;
  }
  const runs = [];
  let i = 0;
  while (i <= n) {
    while (i <= n && !flags[i]) i++;
    if (i > n) break;
    const startIdx = i;
    while (i <= n && flags[i]) i++;
    const endIdx = i - 1;
    runs.push({
      t1: startIdx / n,
      t2: endIdx / n,
      length: ((endIdx - startIdx) / n) * len,
    });
  }
  runs.sort((x, y) => y.length - x.length);
  return runs;
}

/**
 * Clone a wall with its bottom-edge clipped to parametric range
 * [t1, t2] of the original. Keeps the original `id` so doors/windows
 * with `parentWallIds` referencing this wall continue to match, and
 * downstream materialization can look up the wall by its untouched ID.
 *
 * Only `alignment.bottomLeft`, `alignment.bottomRight`, and `center`
 * change — these are the fields `getWallAxes` and the slot pipeline
 * derive geometry from. Other fields (rotation, dimensions, polygon
 * corners) are left intact; downstream code uses them as opaque
 * descriptors and they remain self-consistent for the original wall.
 */
function clipWallToRange(wall, t1, t2) {
  const a = wall.alignment.bottomLeft;
  const b = wall.alignment.bottomRight;
  const newStart = {
    x: a.x + t1 * (b.x - a.x),
    y: a.y,
    z: a.z + t1 * (b.z - a.z),
  };
  const newEnd = {
    x: a.x + t2 * (b.x - a.x),
    y: a.y,
    z: a.z + t2 * (b.z - a.z),
  };
  return {
    ...wall,
    alignment: {
      ...wall.alignment,
      bottomLeft: newStart,
      bottomRight: newEnd,
    },
    center: wall.center
      ? {
          x: (newStart.x + newEnd.x) / 2,
          y: wall.center.y,
          z: (newStart.z + newEnd.z) / 2,
        }
      : wall.center,
  };
}

/**
 * Return the walls (clipped to longest in-kitchen run per wall) that
 * the kitchen-layout pipeline should consider. Replaces the cruder
 * probe-at-midpoint behaviour of `getKitchenSectionWalls` with a
 * geometric test: for each wall, sample along its length, probe
 * inward, and keep the longest contiguous run whose probe lands in
 * the kitchen Voronoi cell's MAIN reachable component (excludes
 * isolated < 3' sub-regions). A wall not originally probe-assigned to
 * the kitchen still contributes its in-kitchen portion; a wall that's
 * only partly in the kitchen contributes only that part.
 *
 * For multi-run walls, the longest run wins — keeps wall IDs unique
 * so doors/windows referencing `parentWallIds` keep matching. Smaller
 * disjoint runs are dropped (typically tiny fragments below a usable
 * cabinet length).
 *
 * Falls back to `getKitchenSectionWalls` for single-section rooms or
 * when no kitchen region can be resolved (no override and no Voronoi cell).
 */
// Drop wall runs shorter than this — too small to fit any base
// cabinet (smallest is ~12"). The boundary-distance filter above
// already excludes spurious slivers from over-extended walls, so
// this just gates against unusable short stretches.
const MIN_WALL_RUN_LENGTH = 0.3;

function getKitchenFinalWalls(roomData) {
  const walls = roomData?.walls || [];
  const sections = roomData?.sections || [];
  // resolveKitchenRegion (via prepareKitchenRegion) is the single
  // source of truth: an explicit room.kitchenArea override wins, else
  // Voronoi/floor as before. Honoring it here lets an override drive a
  // multi-section room even when no section is labeled "kitchen".
  const region = prepareKitchenRegion(roomData);
  if (!region) {
    return sections.length > 1 ? getKitchenSectionWalls(roomData) : walls;
  }
  const out = [];
  for (const wall of walls) {
    // Virtual walls (peninsula synthesizer output) are free-standing —
    // their surface lies INSIDE the kitchen polygon rather than on its
    // boundary, so the boundary-distance probe rejects most of them.
    // Trust the synthesizer's geometry and pass them through.
    if (wall.isVirtual) {
      out.push(wall);
      continue;
    }
    const runs = findKitchenWallRuns(roomData, wall, undefined, region);
    if (!runs || runs.length === 0) continue;
    const best = runs[0];
    if (best.length < MIN_WALL_RUN_LENGTH) continue;
    if (best.t1 <= 1e-6 && best.t2 >= 1 - 1e-6) {
      out.push(wall);
    } else {
      out.push(clipWallToRange(wall, best.t1, best.t2));
    }
  }
  return out;
}

module.exports = {
  assignWallsToSections,
  getKitchenSectionWalls,
  getKitchenBoundaryPolygons,
  getKitchenFinalWalls,
  findKitchenWallRuns,
  clipWallToRange,
  PROBE_OFFSET,
};
